import { useCallback, useContext } from 'react';

import { AxiosResponse } from 'axios';

import { PermissionCatalogueModuleDTO } from '@vyce/core/src/api/types';
import { RolesModuleContext } from '@vyce/core/src/contexts/rolesModuleProvider';

// The catalogue is company-wide and identical for every module panel, so cache it
// for the session instead of re-fetching it on each accordion expand. There is no
// react-query in this repo, hence the plain module-level map.
const cache = new Map<string, PermissionCatalogueModuleDTO[]>();
const inFlight = new Map<string, Promise<AxiosResponse<PermissionCatalogueModuleDTO[]>>>();

export const usePermissionCatalogue = () => {
  const { getCompanyPermissionsCatalogueRequest } = useContext(RolesModuleContext);

  const fetchCatalogue = useCallback(
    async (companyId: string): Promise<PermissionCatalogueModuleDTO[]> => {
      const cached = cache.get(companyId);
      if (cached) return cached;

      const pending = inFlight.get(companyId) || getCompanyPermissionsCatalogueRequest(companyId);
      inFlight.set(companyId, pending);
      try {
        const res = await pending;
        const modules = res.data || [];
        cache.set(companyId, modules);
        return modules;
      } finally {
        inFlight.delete(companyId);
      }
    },
    [getCompanyPermissionsCatalogueRequest]
  );

  return { fetchCatalogue };
};
